'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '@/lib/api';
import { useRouter } from 'next/navigation';

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  isAdmin?: boolean;
  role?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  profileImage?: string;
  contactAddress?: string;
  contactCity?: string;
  contactCountry?: string;
  gotra?: {
    id: number;
    name: string;
    verified: boolean;
  } | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; user?: User }>;
  googleLogin: (idToken: string) => Promise<{ success: boolean; message?: string; user?: User }>;
  signup: (userData: { name: string; email: string; password: string; phone?: string; country?: string }) => Promise<{ success: boolean; message?: string; user?: User }>;
  logout: (options?: { redirect?: boolean }) => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback for non-critical auth check to improve FCP
    const checkAuthDelayed = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          checkAuth();
        }, { timeout: 2000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          checkAuth();
        }, 100);
      }
    };
    
    checkAuthDelayed();
  }, []);

  const checkAuth = async (retryCount = 0) => {
    // Prevent multiple simultaneous auth checks
    if (isCheckingAuth) {
      return;
    }

    try {
      setIsCheckingAuth(true);
      
      // Wait a bit to ensure window and localStorage are available
      if (typeof window === 'undefined') {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        setUser(null);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 No token found in localStorage');
        }
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Checking auth with token:', token.substring(0, 20) + '...');
      }

      // Ensure token is set in API service
      apiService.setToken(token);

      const response: any = await apiService.getCurrentUser();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Auth check response:', response);
      }
      
      if (response?.success && response?.user) {
        setUser(response.user);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ User authenticated:', response.user.email);
        }
      } else {
        // Only clear token if response explicitly indicates auth failure
        // Check for specific auth error messages
        const isAuthError = response?.message?.toLowerCase().includes('unauthorized') ||
                           response?.message?.toLowerCase().includes('invalid token') ||
                           response?.message?.toLowerCase().includes('token expired') ||
                           response?.message?.toLowerCase().includes('access denied') ||
                           response?.message?.toLowerCase().includes('authentication');
        
        if (isAuthError) {
          console.warn('⚠️ Auth check failed - clearing token:', response?.message || 'Invalid token');
          apiService.logout();
          setUser(null);
        } else {
          // For other response errors (not auth-related), keep token but don't update user
          // This handles cases where API returns non-success but token is still valid
          console.warn('⚠️ Auth check returned non-success, but keeping token:', response?.message);
          // Don't clear token - might be a temporary API issue
        }
      }
    } catch (error: any) {
      // Log error for debugging
      console.error('❌ Auth check error:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response
        });
      }
      
      // Check if this is an actual authentication error
      const isAuthError = 
        error.message?.includes('401') || 
        error.message?.includes('403') || 
        error.message?.toLowerCase().includes('unauthorized') || 
        error.message?.toLowerCase().includes('invalid token') ||
        error.message?.toLowerCase().includes('token expired') ||
        error.message?.toLowerCase().includes('access denied') ||
        error.response?.status === 401 ||
        error.response?.status === 403;
      
      // Check if this is a network/timeout error (transient)
      const isTransientError = 
        error.message?.includes('timeout') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Network request failed') ||
        error.name === 'AbortError' ||
        error.isTimeout === true;
      
      if (isAuthError) {
        // Only clear token on actual authentication errors
        console.warn('⚠️ Authentication error - clearing token');
        apiService.logout();
        setUser(null);
      } else if (isTransientError && retryCount < 2) {
        // Retry transient errors up to 2 times with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, retryCount), 3000);
        console.warn(`⚠️ Transient error detected, retrying in ${delay}ms (attempt ${retryCount + 1}/2)`);
        setTimeout(() => {
          checkAuth(retryCount + 1);
        }, delay);
        return; // Don't set loading to false yet - we're retrying
      } else if (isTransientError) {
        // After retries exhausted, keep token but don't update user
        // This prevents logout on temporary network issues
        console.warn('⚠️ Transient error after retries - keeping token, user may need to refresh');
        // Don't clear token - it's likely still valid
        // Don't update user state - keep previous state if available
      } else {
        // For unknown errors, be conservative - don't clear token unless it's clearly an auth error
        console.warn('⚠️ Unknown error during auth check - keeping token to prevent false logout');
        // Don't clear token - might be a temporary issue
      }
    } finally {
      setIsCheckingAuth(false);
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response: any = await apiService.login(email, password);
      if (response?.success && response?.token && response?.user) {
        setUser(response.user);
        // Return user with isAdmin flag for redirect logic
        return { success: true, user: response.user };
      }
      return { success: false, message: response?.message || 'Invalid email or password' };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, message: error.message || 'Login failed. Please try again.' };
    }
  };

  const googleLogin = async (idToken: string) => {
    try {
      const response: any = await apiService.googleLogin(idToken);
      if (response?.success && response?.token && response?.user) {
        setUser(response.user);
        return { success: true, user: response.user };
      }
      return { success: false, message: response?.message || 'Google login failed' };
    } catch (error: any) {
      console.error('Google login error:', error);
      return { success: false, message: error.message || 'Google login failed. Please try again.' };
    }
  };

  const signup = async (userData: { name: string; email: string; password: string; phone?: string; country?: string }) => {
    try {
      const response: any = await apiService.signup(userData);
      if (response?.success && response?.token && response?.user) {
        setUser(response.user);
        // Don't redirect here - let the page handle it
        return { success: true, user: response.user };
      }
      return { success: false, message: response?.message || 'Signup failed. Please try again.' };
    } catch (error: any) {
      console.error('Signup error:', error);
      return { success: false, message: error.message || 'Signup failed. Please try again.' };
    }
  };

  const logout = (options?: { redirect?: boolean }) => {
    apiService.logout();
    setUser(null);
    const shouldRedirect = options?.redirect ?? true;
    if (shouldRedirect) {
      router.push('/');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        googleLogin,
        signup,
        logout,
        refreshUser: checkAuth,
        isAuthenticated: !!user,
        isAdmin: !!user && (user.isAdmin || user.role === 'admin'),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

